<template>
  <form :class="classes">
    <slot />
  </form>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

const name = 'ten-form';
const emptyFn = () => {};

export default {
  name,
  props: {
    /**
     * 表单项目列表
     */
    model: Object,
    /**
     * 表单默认值，重置表单时会被使用，如未提供，创建时 model 的值将被当做表单默认值
     */
    defaultModel: Object,
    /**
     * 定义规则
     */
    rules: Object,
    /**
     * 是否滚动到错误位置
     */
    errorLocation: Boolean,
    /**
     * 滚动到错误位置偏移
     */
    errorLocationOffset: {
      type: Number,
      default: 100,
    },
    /**
     * 布局方式
     * @member default | inline
     */
    layout: String,
    /**
     * 标签对齐方式
     * @member right | left
     */
    labelAlign: {
      type: String,
      default: 'left',
    },
    /**
     * 标签宽度
     */
    labelWidth: {
      type: String,
    },
  },
  provide() {
    return {
      form: this,
    };
  },
  data(props) {
    return {
      fields: [],
      formDefaultModel: props.defaultModel || cloneDeep(props.model),
    };
  },
  computed: {
    classes() {
      return [
        `${name}`,
        `${name}--labelalign-${this.labelAlign}`,
        {
          [`${name}--${this.layout}`]: this.layout,
          [`${name}-${this.size}`]: this.size,
        },
      ];
    },
  },
  watch: {
    rules: {
      handler() {
        this.fields.forEach(field => field.updateRules());
      },
      deep: true,
    },
    defaultModel(v) {
      this.formDefaultModel = v;
    },
  },

  created() {
    this.$on('form-item.add', (field) => {
      if (field) this.fields.push(field);
      return false;
    });
    this.$on('form-item.remove', (field) => {
      if (field.prop) this.fields.splice(this.fields.indexOf(field), 1);
      return false;
    });
  },

  methods: {
    /**
     * 重置表单，默认使用初始化时 model 的值进行重置
     * 如提供 defaultModel 属性则使用该值进行重置
     * @public
     */
    resetFields() {
      if (this.formDefaultModel) {
        this.fields.forEach(field => field.resetField());
      }
    },
    /**
     * 清除表单验证错误
     * @public
     * @param {Array} props 需要清除的字段
     */
    clearValidate(props = []) {
      // eslint-disable-next-line
      const fields = props.length
        ? typeof props === 'string'
          ? this.fields.filter(field => props === field.prop)
          : this.fields.filter(field => props.indexOf(field.prop) > -1)
        : this.fields;
      fields.forEach((field) => {
        field.clearValidate();
      });
    },
    /**
     * 执行 form 验证
     * 支持两种使用方式 promise 和 回调函数
     * @public
     * @param {Function} callback 验证回调回调参数为(isValid, errors)
     */
    validate(callback, _errorLocation) {
      const props = this.fields.map(field => field.prop);
      let errorLocation;
      if (typeof _errorLocation === 'undefined') {
        errorLocation = this.errorLocation; // eslint-disable-line
      }
      return this.validateFields(props, callback, errorLocation);
    },
    /**
     * 验证某个或某几个 formItem
     * @public
     * @param {String|Array} props 待验证的 props 可以是单个 prop 或者多个
     * @param {Function} callback 回调
     * @param {Boolean} errorLocation 同 errorLocation 属性
     * @returns {Promise} 返回验证 promise
     */
    validateFields(props = [], callback, errorLocation) {
      let propsToValidate = props;
      if (!Array.isArray(props)) {
        propsToValidate = [propsToValidate];
      }

      const promise = new Promise((resolve, reject) => {
        let valid = true;
        let firstErrorField;
        const errors = [];
        let count = 0;
        const fieldsToValidate = propsToValidate
          .map(prop => this.fields.find(field => field.prop === prop)).filter(v => v);
        const total = fieldsToValidate.length;

        if (!this.model || total === 0) {
          resolve();
          callback(valid, null);
        }

        const checkValidateAll = () => {
          count += 1;
          if (count === total) {
            if (!valid) {
              if (errorLocation) {
                const errorElem = firstErrorField.$el;
                const top = this.errorLocationOffset || 0;
                const scrollY =                  errorElem.getBoundingClientRect().top
                  + window.pageYOffset
                  - top;
                window.scrollTo(0, scrollY);
              }
              callback && callback(valid, errors);
              reject(errors);
            } else {
              resolve();
              callback && callback(valid, null);
            }
          }
        };

        fieldsToValidate.forEach((field) => {
          field.validate().then(() => {
            checkValidateAll();
          })
            .catch((errs) => {
              const error = { prop: field.prop, message: errs[0].message };
              valid = false;
              firstErrorField = firstErrorField || field;
              errors.push(error);
              checkValidateAll();
            });
        });
      });
      promise.catch(emptyFn);
      return promise;
    },
  },
};
</script>
